<template>
  <v-container
    v-if="display"
    id="app-index"
    class="pt-2"
  >
    <tabs
      v-if="!displayShopList"
      v-model="tab"
      :headers="[
        $t('field.user'),
        $t('field.term')
      ]"
    >
      <!-- ***************************
      ********** CONNECTION **********
      **************************** -->
      <user-connection
        slot="tab_0"
        @valid="connection"
      />
      <!-- *******************************
      ********** INITIALISATION **********
      ******************************** -->
      <terminal-connection
        slot="tab_1"
        @valid="initialisation"
      />
    </tabs>
    <!-- **********************
    ********** SHOPS **********
    *********************** -->
    <div
      v-else
      id="index-shop"
      class="pa-1"
    >
      <div class="d-flex align-center mb-2">
        <v-text-field
          v-model="filter"
          :placeholder="$t('field.search')"
          hide-details
          outlined
          class="title pb-0 ma-0 needsclick"
        />
        <v-btn
          color="error"
          class="ml-2"
          style="height: 54px"
          @click="disconnection"
        >
          <v-icon medium>
            {{ IconEnum.disconnection }}
          </v-icon>
        </v-btn>
      </div>

      <scrollable full>
        <v-btn
          v-for="(shop, index) in shopsFiltered"
          :key="index"
          :name="shop.Enseigne"
          class="mb-2"
          x-large
          block
          @click="launchInit(shop, true)"
        >
          {{ shop.Enseigne }}
        </v-btn>
      </scrollable>
    </div>

    <v-btn
      v-if="!IS_DESKTOP"
      absolute
      bottom
      right
      icon
      large
      class="d-none d-sm-block"
      @click="changeOrientation"
    >
      <v-icon medium>
        screen_rotation
      </v-icon>
    </v-btn>

    <!-- ***** FOOTER ****** -->
    <app-footer />
  </v-container>
</template>

<script>
import { MetierLocalStorage } from 'modulelocalstorage'
import { Initialisation } from '@/modules/Initialisation'
import { IconEnum, IntentAction } from '@/modules/Enums'
import { version } from '../../package.json'

import AppFooter from '@/components/core/Footer'
import UserConnection from '@/components/form/UserConnection'
import { $alert, $closeKeyboard, getIntent, getNextOrientation } from '@/modules/Utils'
import Tabs from '@/components/Tabs'
import TerminalConnection from '@/components/form/TerminalConnection'
import { $getNumberOfTransactions } from '@/modules/Transactions'

export default {
  name: 'IndexView',

  components: {
    TerminalConnection,
    Tabs,
    UserConnection,
    AppFooter
  },

  data () {
    return {
      IconEnum,
      IS_DESKTOP,
      display: false,
      tab: 0,
      //
      filter: '',
      shops: []
    }
  },

  computed: {
    displayShopList () { return this.shops.length !== 0 },
    shopsFiltered () {
      return this.shops.filter(shop => {
        return shop.Enseigne ? shop.Enseigne.toNormalize().includes(this.filter.toNormalize()) : false
      })
    }
  },

  async created () {
    this.$emit('title', this.$i18n.t('title.initialisation'))

    // Définition BUILDER KEY
    if (!MetierLocalStorage.getBuilderKey()) {
      MetierLocalStorage.setBuilderKey('WEB002')
    }

    let teleparameters = MetierLocalStorage.getTeleparametres()

    if (IS_DESKTOP) {
      MetierLocalStorage.setPlatformBrowser()

      const query = this.$route.query

      // ***** Contrôle intégration SITES *****
      // Check environnement
      if (query.env) {
        const env = parseInt(query.env)
        if (!isNaN(env && env >= 0 && env <= 2)) {
          MetierLocalStorage.setEnv(env)
          this.$emit('change-env', env)
        }
      }
      // Check connexion
      if (query.pdv && query.licence) {
        const pdv = parseInt(query.pdv)
        if (isNaN(pdv)) {
          $alert(this.$i18n.t('error.pdv_unknown'))
          this.display = true
        } else {
          // Si nouvelle connexion ou point de vente différent de l'actuel, on initialise
          if (teleparameters === null || pdv !== MetierLocalStorage.getIdPointDeVente()) {
            this.initialisation(query.pdv, query.licence, '992', true)
            return
          }
        }
      } else if (query.deconnection !== undefined && query.deconnection === 'true') {
        this.disconnection()
        return
      }
    }

    // Redirection par défaut vers la page d'accueil
    let pageRedirect = '/home'

    if (IS_ANDROID) {
      // Check external call intent
      const intent = await getIntent()

      if (intent.intentAction === IntentAction.INIT) {
        // *** Appel extérieur pour effectuer une action ***

        if (!intent.posId || !intent.licence || !intent.rank) {
          // Si on ne reçoit pas les bons paramètres, on retourne une erreur
          CissAndroid.sendError({ message: 'posID, licence ou rank est manquant' })
        } else if (MetierLocalStorage.getIdPointDeVente() === intent.posId) {
          // Si on est déjà paramétré sur ce point de vente, on renvoie un succès avec les paramètres
          CissAndroid.sendSuccess({
            ...Initialisation.getLogins(),
            posInfos: teleparameters.InfosPDV,
            params: teleparameters.BoostoreService
          })
        } else {
          // Sinon, on lance une nouvelle initialisation
          this.initialisation(intent.posId, intent.licence, intent.rank, intent.replace ? intent.replace : false)
        }
        // Dans tous les cas, on s'arrête ici
        return
      } else if (intent.intentAction === IntentAction.TRANSACTION) {
        // *** Appel extérieur pour effectuer une action ***

        if (teleparameters === null) {
          // Si on n'est pas télé-paramétré, on retourne une erreur
          CissAndroid.sendError({ message: 'myTerminal n\'est pas initialisé' })
          return
        } else {
          // Mise en mémoire du montant
          this.$store.commit('amount', parseInt(intent.amount))
          // Redirection vers la page de choix d'action
          pageRedirect = '/action-choice'
        }
      } else if (intent.intentAction === IntentAction.CLOSING) {
        // *** Appel extérieur pour effectuer un rapport de fin de journée ***

        if (teleparameters === null) {
          // Si on n'est pas télé-paramétré, on retourne une erreur
          CissAndroid.sendError({ message: 'myTerminal n\'est pas initialisé' })
          return
        } else {
          // Redirection vers la page de télé-collecte
          pageRedirect = '/closing'
        }
      } else if (intent.intentAction === IntentAction.SETTINGS) {
        // *** Appel extérieur pour afficher les paramètres ***

        if (teleparameters === null) {
          // Si on n'est pas télé-paramétré, on va vers la page des paramètres directement
          await this.$router.replace({ name: 'settings', params: { checkConnected: false } })
          return
        } else {
          // Redirection vers les paramètres
          pageRedirect = '/settings'
        }
      }
    }

    let shops = MetierLocalStorage.getShops()

    // ****************************************
    // ********** PREMIERE CONNEXION **********
    //*****************************************
    if (teleparameters === null) {
      // Stockage du numéro de version actuelle
      localStorage.setItem('mytmversion', version)

      // Contrôle appareil PAX bancaire
      if (IS_PAX) {
        MetierLocalStorage.setPaxInfos(null)
      }

      if (shops.length > 0) {
        this.$emit('title', this.$i18n.t('title.index_pdv'))
        this.shops = shops
        this.$store.commit('shops', shops)
      }

      this.display = true
    } else {
      // **********************************
      // ***** Contrôle de la version *****
      // **********************************
      let oldVersion = localStorage.getItem('mytmversion')
      let oldMinorVersion = oldVersion === null ? 0 : parseInt(oldVersion.split('.')[1])
      let refreshParameters = false

      if (oldMinorVersion < 23) {
        // Suppression des actions disponibles pour nouveau formatage
        MetierLocalStorage.setActions(null)
        MetierLocalStorage.setMenuItems(null)
        MetierLocalStorage.setFavoriteItem(null)
        refreshParameters = true
      }
      // Stockage nouvelle version actuelle
      localStorage.setItem('mytmversion', version)

      // ***************************************
      // ***** Mise en mémoire du stockage *****
      // ***************************************
      let creditPermissions = MetierLocalStorage.getRights()
      this.$store.commit('merchantName', MetierLocalStorage.getNomMarchand())
      this.$store.commit('userName', MetierLocalStorage.getNomUser())
      this.$store.commit('creditPermissions', creditPermissions)
      this.$store.commit('shops', shops)
      this.$store.commit('listproducts', MetierLocalStorage.getProducts())
      this.$store.commit('consignes', MetierLocalStorage.getConsignes())
      this.$store.commit('offline', MetierLocalStorage.getOffline())
      this.$store.commit('event', MetierLocalStorage.getEvent())
      this.$store.commit('usepassword', MetierLocalStorage.getUsePwd())

      let neptingInfos = MetierLocalStorage.getPaxInfos()
      if (neptingInfos) {
        this.$store.commit('neptingInfos', neptingInfos)
      }

      // Drawer options
      let options = MetierLocalStorage.getDrawerOptions()
      if (options !== null) {
        this.$store.commit('drawerOptions', options)
      }

      // Boutons d'actions
      let actions = MetierLocalStorage.getActions()
      if (actions === null) {
        actions = Initialisation.formatActions(teleparameters, creditPermissions)
      }
      this.$store.commit('actionItems', actions)

      // Boutons menu touche raccourci
      let menuItems = MetierLocalStorage.getMenuItems()
      if (menuItems === null) {
        menuItems = Initialisation.formatShorcutActions(teleparameters)
      }
      this.$store.commit('menuItems', menuItems)

      // Action favorite menu rapide
      let favorite = MetierLocalStorage.getFavoriteItem()
      if (favorite === null) {
        favorite = menuItems.length - 1
        MetierLocalStorage.setFavoriteItem(favorite)
      }
      this.$store.commit('favoriteItem', favorite)

      // Type de scan favori
      favorite = MetierLocalStorage.getFavoriteScan()
      if (favorite !== null) {
        this.$store.commit('favoriteScan', favorite)
      }

      // Consigne/Déconsigne
      let idConsigne = localStorage.getItem('idConsigne')
      let idDeconsigne = localStorage.getItem('idDeonsigne')
      this.$store.commit('idConsigne', idConsigne === null ? 0 : parseInt(idConsigne))
      this.$store.commit('idDeconsigne', idDeconsigne === null ? 0 : parseInt(idDeconsigne))

      // Utilisation du plugin cashless
      if (MetierLocalStorage.getSansContact()) {
        Cashless.startService()
      }

      if (refreshParameters) {
        // Définition is gift only
        teleparameters.isGiftOnly = teleparameters.useGift && !teleparameters.VCH && actions.length === 0
        // Sauvegarde nouveau paramétrage
        MetierLocalStorage.setTeleparametres(teleparameters)
      }
      this.$store.commit('teleparameters', teleparameters)

      // Lancement du service d'envoi des transactions offline
      $getNumberOfTransactions()

      // Redirection vers la page Home
      this.$router.replace(pageRedirect)
    }
  },

  methods: {
    changeOrientation () {
      const newOrientation = getNextOrientation()
      screen.orientation.lock(newOrientation)
      MetierLocalStorage.setScreenOrientation(newOrientation)
    },
    reset (msg) {
      $closeKeyboard(this.$nextTick)
      this.shops = []
      if (msg) $alert(msg)
    },
    connection (user, pass) {
      Initialisation.connection(
        user,
        pass,
        (res) => {
          // Plusieurs magasins (sinon init direct)
          this.shops = res
        },
        (error) => {
          this.reset(error)
        },
        true
      )
    },
    initialisation (pdv, licence, rank, replace) {
      this.launchInit(
        {
          'IDPointDeVente': pdv,
          'Licence': licence,
          'Rang': rank,
          'binDroits': ''
        },
        replace
      )
    },
    launchInit (shop, replace) {
      Initialisation.prepareInitialisation(shop,
        (e) => {
          // Si on a un intent en mémoire, on retourne OK
          if (this.$store.getters.externalAction === IntentAction.INIT) {
            CissAndroid.sendError({ message: e })
            return
          }

          this.display = true
          if (this.displayShopList) {
            $alert(e)
          } else {
            MetierLocalStorage.setIdUser(null)
            this.reset(e)
          }
        },
        replace
      )
    },
    disconnection () {
      MetierLocalStorage.clear()
      this.$store.dispatch('clear')
      this.shops = []
      this.$emit('title', this.$i18n.t('title.initialisation'))
    }
  }
}
</script>
